import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, AGE_RANGES, CAP_LABELS, categoryFor, fmtErr } from "@/lib/api";
import { Wordmark } from "@/components/Wordmark";
import ScoreGauge from "@/components/ScoreGauge";

const EASE = [0.22, 1, 0.36, 1];

const STRONG_COPY = (cap) =>
  `Your everyday ${CAP_LABELS[cap]?.toLowerCase()} appears to be one of your stronger physical capabilities.`;
const WEAK_COPY = {
  push: "Building pressing strength may make everyday pushing tasks feel noticeably easier.",
  pull: "Developing back and grip strength may help with carrying and pulling in daily life.",
  squat: "Building lower-body strength and control may make sitting, standing and stairs easier.",
  bend_lift: "Developing hip movement and trunk control may improve your everyday lifting capability.",
  rotation: "Improving rotational control and thoracic mobility may make reaching and turning feel freer.",
  endurance: "Building aerobic capacity gradually may help you move longer with less fatigue.",
};

export default function PublicAssessment() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("q"); // q | analyzing | lead | result
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({ name: "", email: "", age_range: "", phone: "", marketing_consent: false });
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/questions").then((r) => setData(r.data)).catch(() => setErr("Could not load the assessment."));
  }, []);

  const answer = (v) => {
    const q = data.questions[step];
    const next = { ...answers, [q.key]: v };
    setAnswers(next);
    if (step < data.questions.length - 1) setStep(step + 1);
    else {
      setPhase("analyzing");
      setTimeout(() => setPhase("lead"), 1400);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data: res } = await api.post("/public/assessment", { ...lead, answers });
      localStorage.setItem("c360_participant", JSON.stringify({
        participant_id: res.participant_id, name: res.name, email: res.email,
        phone: res.phone, age_range: res.age_range, overall: res.overall,
      }));
      setResult(res);
      setPhase("result");
      window.scrollTo(0, 0);
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally {
      setLoading(false);
    }
  };

  const share = async () => {
    const text = `My Capable 360 Everyday Capability Score: ${result.overall}/100. How capable are you? Take the free 2-minute assessment: ${window.location.origin}/assessment`;
    if (navigator.share) {
      try { await navigator.share({ title: "Capable 360", text }); return; } catch { /* cancelled */ }
    }
    await navigator.clipboard.writeText(text);
    toast.success("Score copied — paste it anywhere to share.");
  };

  if (err && !data) return <Shell><p className="text-zinc-400">{err}</p></Shell>;
  if (!data) return <Shell><p className="text-zinc-500 animate-pulse text-xs uppercase tracking-[0.25em]">Loading assessment…</p></Shell>;

  const total = data.questions.length;
  const q = data.questions[step];
  const progress = Math.round((Object.keys(answers).length / total) * 100);

  return (
    <Shell>
      <AnimatePresence mode="wait">
        {phase === "q" && (
          <motion.div key={`q${step}`} initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -60 }} transition={{ duration: 0.35, ease: EASE }} className="w-full max-w-2xl">
            <div className="flex items-center justify-between mb-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
              <span data-testid="assessment-progress-label">Question {step + 1} of {total}</span>
              <span>{progress}%</span>
            </div>
            <div className="h-1 bg-zinc-800 mb-12">
              <div className="h-full bg-volt transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <h1 className="font-display uppercase text-4xl sm:text-6xl leading-[0.95] min-h-[2em]" data-testid="assessment-question">
              {q.text}
            </h1>
            <div className="mt-10 flex flex-col gap-3">
              {data.scale.map((s) => (
                <button key={s.value} data-testid={`answer-${s.value}`} onClick={() => answer(s.value)}
                  className="border border-zinc-700 px-6 py-5 text-left font-display uppercase text-2xl tracking-wide hover:bg-volt hover:text-black hover:border-volt transition-colors duration-200">
                  {s.label}
                </button>
              ))}
            </div>
            {step > 0 && (
              <button data-testid="assessment-back" onClick={() => setStep(step - 1)}
                className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-volt transition-colors">
                <ArrowLeft size={14} /> Back
              </button>
            )}
          </motion.div>
        )}

        {phase === "analyzing" && (
          <motion.div key="analyzing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="text-center" data-testid="assessment-analyzing">
            <p className="font-display uppercase text-4xl sm:text-6xl animate-pulse">Analysing your capabilities…</p>
          </motion.div>
        )}

        {phase === "lead" && (
          <motion.div key="lead" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }} className="w-full max-w-md">
            <h1 className="font-display uppercase text-5xl leading-[0.9]">Your Capable 360 <span className="text-volt">score is ready.</span></h1>
            <p className="mt-4 text-zinc-400 text-sm">Enter your details to reveal it.</p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <input data-testid="lead-name" required placeholder="Full name *" value={lead.name}
                onChange={(e) => setLead({ ...lead, name: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-700 px-5 py-4 text-white placeholder-zinc-600 focus:border-volt outline-none" />
              <input data-testid="lead-email" required type="email" placeholder="Email address *" value={lead.email}
                onChange={(e) => setLead({ ...lead, email: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-700 px-5 py-4 text-white placeholder-zinc-600 focus:border-volt outline-none" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">Age range *</p>
                <div className="grid grid-cols-4 gap-2">
                  {AGE_RANGES.map((a) => (
                    <button type="button" key={a} data-testid={`lead-age-${a}`} onClick={() => setLead({ ...lead, age_range: a })}
                      className={`border px-2 py-3 font-display text-xl transition-colors ${lead.age_range === a ? "bg-volt text-black border-volt" : "border-zinc-700 text-zinc-300 hover:border-volt"}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
              <input data-testid="lead-phone" placeholder="Phone (optional)" value={lead.phone}
                onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-700 px-5 py-4 text-white placeholder-zinc-600 focus:border-volt outline-none" />
              <label className="flex items-start gap-3 text-xs text-zinc-400 cursor-pointer">
                <input type="checkbox" data-testid="lead-consent" checked={lead.marketing_consent}
                  onChange={(e) => setLead({ ...lead, marketing_consent: e.target.checked })}
                  className="mt-0.5 accent-[#ff5c1a] w-4 h-4" />
                I would like to receive Capable 360 training tips, updates and upcoming session information.
              </label>
              {err && <p data-testid="lead-error" className="text-red-400 text-sm">{err}</p>}
              <button type="submit" data-testid="lead-submit" disabled={loading || !lead.age_range}
                className="w-full bg-volt text-black uppercase tracking-[0.18em] text-sm font-extrabold py-5 hover:bg-white transition-colors disabled:opacity-50">
                {loading ? "Revealing…" : "Reveal my score"}
              </button>
            </form>
          </motion.div>
        )}

        {phase === "result" && result && (
          <motion.div key="result" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }} className="w-full max-w-3xl" data-testid="assessment-result">
            <div className="text-center">
              <Wordmark className="text-2xl" />
              <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500">Your Everyday Capability Score</p>
              <div className="mt-8"><ScoreGauge score={result.overall} /></div>
              <p className="mt-4 font-display uppercase text-2xl sm:text-3xl text-volt tracking-wide" data-testid="result-category">
                {result.category}
              </p>
              <p className="text-zinc-500 text-xs mt-1 uppercase tracking-[0.2em]">Age range: {result.age_range}</p>
              {result.client_code && (
                <p data-testid="result-client-code" className="mt-2 text-xs uppercase tracking-[0.25em] text-zinc-400">
                  Your Capable 360 ID: <span className="text-volt font-bold">{result.client_code}</span>
                </p>
              )}
            </div>

            <div className="mt-12 border border-zinc-800 bg-zinc-950 p-6 sm:p-10">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-6">Capability snapshot — indicators from your 8 answers</p>
              <div className="space-y-4">
                {Object.entries(result.capabilities).map(([cap, v], i) => (
                  <div key={cap} data-testid={`result-bar-${cap}`}>
                    <div className="flex justify-between text-xs uppercase tracking-[0.14em] font-bold mb-1.5">
                      <span className="text-zinc-300">{CAP_LABELS[cap]}</span>
                      <span className="text-volt font-display text-base">{v}</span>
                    </div>
                    <div className="h-1.5 bg-zinc-800 overflow-hidden">
                      <motion.div className="h-full bg-volt" initial={{ width: 0 }} animate={{ width: `${v}%` }}
                        transition={{ duration: 1, delay: 0.3 + i * 0.08, ease: EASE }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div className="border border-volt/40 bg-zinc-950 p-7">
                <p className="text-[10px] uppercase tracking-[0.25em] text-volt font-bold">Your strongest area</p>
                <p className="font-display uppercase text-4xl mt-2" data-testid="result-strongest">{CAP_LABELS[result.strongest]}</p>
                <p className="text-sm text-zinc-400 mt-3">{STRONG_COPY(result.strongest)}</p>
              </div>
              <div className="border border-zinc-800 bg-zinc-950 p-7">
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Opportunity to develop</p>
                <p className="font-display uppercase text-4xl mt-2" data-testid="result-weakest">{CAP_LABELS[result.weakest]}</p>
                <p className="text-sm text-zinc-400 mt-3">{WEAK_COPY[result.weakest]}</p>
              </div>
            </div>

            <div className="mt-6 border border-zinc-800 bg-zinc-950 p-7">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">What your score means</p>
              <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                A score of {result.overall} places you in “{categoryFor(result.overall).toLowerCase()}”.
                Your next step: train your weaker capabilities ({CAP_LABELS[result.weakest]}) while keeping
                your strengths sharp — small, consistent sessions beat occasional heroic ones.
              </p>
              <p className="text-[11px] text-zinc-600 mt-4 leading-relaxed">
                The Capable 360 Everyday Capability Score is designed for general fitness education and is
                not a medical assessment or diagnosis.
              </p>
            </div>

            <div className="mt-10 text-center">
              <h2 className="font-display uppercase text-4xl sm:text-5xl">What's your <span className="text-volt">next move?</span></h2>
              <div className="mt-8 grid sm:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
                <a data-testid="result-personalized-cta"
                  href={`https://wa.me/919811783739?text=${encodeURIComponent("Hi Capable 360, I'm interested in personalized training and would like to know more.")}`}
                  target="_blank" rel="noopener noreferrer"
                  className="group border border-volt/50 bg-zinc-950 p-6 hover:bg-volt transition-colors duration-300">
                  <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-volt group-hover:text-black/60">Personalized training</p>
                  <p className="font-display uppercase text-2xl mt-2 group-hover:text-black">Get a plan built around your assessment</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-extrabold text-volt group-hover:text-black">
                    Build my plan <ArrowRight size={14} />
                  </span>
                </a>
                <button data-testid="result-group-cta" onClick={() => navigate("/sessions")}
                  className="group border border-zinc-700 bg-zinc-950 p-6 text-left hover:border-volt transition-colors duration-300">
                  <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-zinc-500">Group sessions</p>
                  <p className="font-display uppercase text-2xl mt-2">Train the system with a coached group</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-extrabold text-zinc-300 group-hover:text-volt">
                    Explore group sessions <ArrowRight size={14} />
                  </span>
                </button>
              </div>
              <button data-testid="result-share-cta" onClick={share}
                className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-zinc-400 hover:text-volt transition-colors">
                <Share2 size={14} /> Share my score
              </button>
              <Link to="/" className="inline-block mt-8 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-volt transition-colors">
                Back to home
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-ink text-white flex flex-col">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="flex items-center justify-between px-5 sm:px-10 h-16 border-b border-zinc-800/70">
        <Link to="/" data-testid="assessment-home-link"><Wordmark className="text-2xl" /></Link>
        <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Free · 2 minutes · 8 questions</span>
      </header>
      <main className="flex-1 flex items-center justify-center px-5 py-14">{children}</main>
    </div>
  );
}
